import {
  arrIncludesArrayItem,
  Filter,
  Button,
  numberInRange,
  TableContext,
} from '@eye-able/ui';
import type { TableContextType, TableRef, TableState } from '@eye-able/ui';
import {
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import type {
  AccessorKeyColumnDef,
  ColumnDef,
  ColumnFiltersState,
  RowData,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';
import { LayoutGrid, List } from 'lucide-react';
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
  type Ref,
  type ComponentType,
} from 'react';
import TableItemCard from '../Card/TableItemCard';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

export type CardTableView = 'list' | 'grid';

export type CardTableRowRendererProps<TData extends RowData> = {
  row: TData;
  view: CardTableView;
};

export type CardTableProps<TData extends RowData> = {
  data?: TData[];
  columns?: ColumnDef<TData, unknown>[];
  searchColumnId?: string;
  defaultSortBy?: string;
  defaultSortDirection?: 'asc' | 'desc';
  defaultView?: CardTableView;
  onRowClick?: (row: TData) => void;
  getRowId?: (row: TData, index: number) => string | number;
  rowTitle: ComponentType<CardTableRowRendererProps<TData>>;
  rowMain: ComponentType<CardTableRowRendererProps<TData>>;
};

interface CardTableComponent {
  <TData extends RowData = RowData>(
    props: CardTableProps<TData> & { ref?: Ref<TableRef<TData>> },
  ): JSX.Element;
}

function getColumnId<TData extends RowData>(
  column: ColumnDef<TData, unknown> | undefined,
): string | undefined {
  if (!column) return undefined;
  const accessorColumn = column as AccessorKeyColumnDef<TData, unknown>;
  return (
    column.id ??
    (typeof accessorColumn.accessorKey === 'string'
      ? accessorColumn.accessorKey
      : undefined)
  );
}

function toCsvValue(value: unknown): string {
  if (value === null || value === undefined) return '';
  const str = String(value).replaceAll('"', '""');
  return /[",\n]/.test(str) ? `"${str}"` : str;
}

function downloadCsv(filename: string, csvContent: string) {
  const blob = new Blob([csvContent], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function CardTableInner<TData extends RowData>(
  {
    data = [],
    columns = [],
    searchColumnId,
    defaultSortBy,
    defaultSortDirection = 'asc',
    defaultView = 'list',
    onRowClick,
    getRowId = (row, index) => {
      const candidate = (row as { id?: string | number }).id;
      return candidate ?? index;
    },
    rowTitle: RowTitle,
    rowMain: RowMain,
  }: CardTableProps<TData>,
  ref: Ref<TableRef<TData>>,
) {
  const { t } = useInlineTranslation();

  const resolvedSearchColumnId = useMemo(() => {
    return searchColumnId ?? getColumnId(columns[0]);
  }, [columns, searchColumnId]);

  const initialSortColumnId = useMemo(() => {
    const firstSortableColumn = columns.find(
      (column) => column.enableSorting !== false && getColumnId(column),
    );
    return defaultSortBy ?? getColumnId(firstSortableColumn);
  }, [columns, defaultSortBy]);

  const [view, setView] = useState<CardTableView>(defaultView);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [sorting, setSorting] = useState<SortingState>(() => {
    if (!initialSortColumnId) return [];

    return [
      {
        id: initialSortColumnId,
        desc: defaultSortDirection === 'desc',
      },
    ];
  });

  const table = useReactTable<TData>({
    data,
    columns,
    state: {
      globalFilter,
      columnFilters,
      columnVisibility,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const normalizedFilter = String(filterValue ?? '')
        .trim()
        .toLowerCase();

      if (!normalizedFilter || !resolvedSearchColumnId) return true;

      const columnValue = String(row.getValue(resolvedSearchColumnId) ?? '')
        .trim()
        .toLowerCase();
      return columnValue.includes(normalizedFilter);
    },
    filterFns: {
      arrIncludesArrayItem: (row, columnId, filterValues, addMeta) => {
        if (arrIncludesArrayItem(row, columnId, filterValues, addMeta)) {
          return true;
        }

        if (!Array.isArray(filterValues) || filterValues.length === 0) {
          return false;
        }

        const cellValue = row.getValue(columnId);
        if (Array.isArray(cellValue)) {
          return cellValue.some((value) => filterValues.includes(value));
        }

        if (typeof cellValue === 'string') {
          const normalizedCellValues = cellValue
            .split(',')
            .map((value) => value.trim())
            .filter(Boolean);
          return normalizedCellValues.some((value) =>
            filterValues.includes(value),
          );
        }

        return false;
      },
      numberInRange,
    },
  });

  const filteredAndSortedRows = table.getSortedRowModel().rows;

  const tableId = useMemo(() => {
    if (typeof window === 'undefined') return 'tabler_table';
    const columnsKey = columns.map(getColumnId).filter(Boolean).join('_');
    const path = window.location.pathname.replaceAll('/', '_');
    return `${path}_tabler_${columnsKey || 'table'}`;
  }, [columns]);

  const tableState = useMemo<TableState>(() => {
    return {
      sorting,
      columnFilters,
      columnVisibility,
    };
  }, [sorting, columnFilters, columnVisibility]);

  const contextValue = useMemo<Partial<TableContextType<TData>>>(() => {
    return {
      globalFilter,
      setGlobalFilter,
      table,
      tableState,
    };
  }, [globalFilter, table, tableState]);

  useImperativeHandle(
    ref,
    () => ({
      table,
      tableId,
      exportTableData: () => {
        const exportableColumns = columns.filter((column) =>
          getColumnId(column),
        );
        const headers = exportableColumns.map((column) => {
          const header = column.header;
          return typeof header === 'string'
            ? header
            : String(getColumnId(column) ?? '');
        });
        const rows = table.getPrePaginationRowModel().rows.map((row) => {
          return exportableColumns.map((column) => {
            const columnId = getColumnId(column);
            return toCsvValue(columnId ? row.getValue(columnId) : '');
          });
        });

        const csvContent = [
          headers.map(toCsvValue).join(','),
          ...rows.map((row) => row.join(',')),
        ].join('\n');

        downloadCsv(tableId || 'export', csvContent);
      },
    }),
    [columns, table, tableId],
  );

  return (
    <TableContext.Provider
      value={contextValue as unknown as TableContextType<unknown>}
    >
      <div className='eye:flex eye:w-full eye:flex-col'>
        <Filter
          disableFullscreen
          disableHideShowColumns
          actions={
            <Button
              type='button'
              variant='ghost'
              size='sm'
              aria-pressed={view === 'grid'}
              onClick={() =>
                setView((prev) => (prev === 'list' ? 'grid' : 'list'))
              }
            >
              {view === 'list' ? (
                <LayoutGrid size={16} aria-hidden />
              ) : (
                <List size={16} aria-hidden />
              )}
              {t(view === 'list' ? 'gridView' : 'listView')}
            </Button>
          }
        />

        {!filteredAndSortedRows.length ? (
          <div className='eye:body-m eye:py-16 eye:text-center eye:text-grey-100'>
            {t('noResultsFound')}
          </div>
        ) : (
          <div className='eye:w-full eye:overflow-auto eye:max-h-[70dvh] eye:p-4xs'>
            <div
              className={
                view === 'grid'
                  ? 'eye:grid eye:grid-cols-1 eye:gap-2xs eye:md:grid-cols-2 eye:xl:grid-cols-3'
                  : 'eye:flex eye:flex-col eye:gap-2xs'
              }
            >
              {filteredAndSortedRows.map((tableRow, index) => {
                const row = tableRow.original;
                const rowId = getRowId(row, index);

                return (
                  <TableItemCard
                    key={rowId}
                    title={<RowTitle row={row} view={view} />}
                    main={<RowMain row={row} view={view} />}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </TableContext.Provider>
  );
}

const CardTable = forwardRef(CardTableInner) as CardTableComponent;

export default CardTable;
